import { z } from "zod";
import { IsoDateSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * PREPARED FOOD INVENTORY (ADR-065, Prepared Batch Inventory v1) — food the
 * user has CONFIRMED they cooked, not yet eaten. It is a third, distinct
 * concept:
 *
 *   Raw Pantry Inventory  — confirmed ingredients, available as ingredients
 *   Prepared Food Inventory — confirmed cooked portions, no longer available
 *                             as arbitrary raw ingredients (this file)
 *   Pantry Carryover       — a forecast economic value, never inventory
 *
 * Preparation is always an explicit user action: the planner scheduling a
 * batch never creates prepared food. A skipped, swapped or restaurant-
 * replaced meal never consumes a portion.
 */
export const PreparedBatchStateSchema = z.enum(["active", "depleted"]);
export const PreparedBatchSchema = z.object({
    id: z.string(),
    /** The plan payload's batch key — stable across revisions of the same week. */
    batchKey: z.string(),
    recipeId: z.string().nullable(),
    /** Localized recipe title when the catalog knows it (never invented). */
    displayName: z.string(),
    weekStartDate: IsoDateSchema,
    preparedAt: IsoDateTimeSchema,
    portionsPrepared: z.number().positive(),
    /** Fractional consumption is supported; never negative, never above portionsPrepared. */
    portionsRemaining: z.number().nonnegative(),
    /** Derived from portionsRemaining by the database, never set by a client. */
    state: PreparedBatchStateSchema,
    /**
     * The meal ids of the CURRENT plan this batch supplies, when the week's
     * plan still schedules it — a deterministic relationship, never an
     * automatic scheduler (V1 boundary).
     */
    scheduledMealIds: z.array(z.string()),
});
export const PreparedBatchesResponseSchema = z.object({
    items: z.array(PreparedBatchSchema),
    /**
     * Batches the current plan schedules that have NOT been marked prepared —
     * what the user can still confirm. Never inventory.
     */
    preparable: z.array(z.object({
        batchKey: z.string(),
        recipeId: z.string().nullable(),
        displayName: z.string(),
        weekStartDate: IsoDateSchema,
        /** The portions the plan schedules for this batch (the confirmation default). */
        plannedPortions: z.number().positive(),
        mealIds: z.array(z.string()),
    })),
});
// POST /v1/prepared-batches — "I cooked this batch."
export const PrepareBatchRequestSchema = z.object({
    /** The batch key served on the plan's meals as `batchComponentId`. */
    batchKey: z.string(),
    /** Stale-write guard against the plan revision the user confirmed from. */
    baseRevision: z.number().int().nonnegative(),
    /** Portions actually cooked; defaults to the portions the plan schedules for this batch. */
    portions: z.number().positive().optional(),
});
export const PrepareBatchResponseSchema = z.object({
    batch: PreparedBatchSchema,
    /**
     * What the raw-ingredient deduction actually did. `uncoveredIngredients`
     * names ingredients the confirmed Pantry did not cover: the truth is
     * reported, never invented as stock and never driven negative.
     */
    pantrySync: z.object({
        deductedIngredients: z.number().int().nonnegative(),
        uncoveredIngredients: z.array(z.string()),
    }),
});
//# sourceMappingURL=preparedBatches.js.map